import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { logisticsApi } from "./ApiClient";
import { generateQueryString } from "./Helpers";
import {
  medicalCompetenceSchema,
  shiftTypeSchema,
} from "@medoma/models/shifts";
import { categorySchema } from "@medoma/models/activities";
import { calendarDateSchema } from "@medoma/models/date-and-time";

const categoryCountSchema = z.array(
  z.object({ category: categorySchema, count: z.int().nonnegative() }),
);

const shiftsByTypeSchema = z.array(
  z.object({
    type: shiftTypeSchema,
    staffedCount: z.int().nonnegative(),
    notStaffedCount: z.int().nonnegative(),
    staffedHours: z.number().nonnegative(),
    notStaffedHours: z.number().nonnegative(),
  }),
);

const competenceCountsSchema = z.array(
  z.object({
    competence: medicalCompetenceSchema,
    shiftsByType: shiftsByTypeSchema,
  }),
);

export const dailyOverviewDataSchema = z.object({
  date: z.string(),
  admittedPatientsCount: z.number().nonnegative(),
  competenceCapacity: z.array(
    z.object({
      competence: medicalCompetenceSchema,
      categoryCounts: categoryCountSchema,
      shiftsByType: shiftsByTypeSchema,
    }),
  ),
});

export const daySummaryDataSchema = z.object({
  admittedPatientsCount: z.int().nonnegative(),
  plannedAdmissionPatientsCount: z.int().nonnegative(),
  externalPatientsCount: z.int().nonnegative(),
  assessmentPatientsCount: z.int().nonnegative(),
  plannedDischargePatientsCount: z.int().nonnegative(),
  patientsWithVisitsCount: z.int().nonnegative(),
  categoryCounts: categoryCountSchema,
  competenceCounts: competenceCountsSchema,
});

export const partOfDaySchema = z.enum(["day", "evening", "night"]);
export type PartOfDay = z.infer<typeof partOfDaySchema>;

const partOfDayCapacitySchema = z.object({
  shiftsByType: shiftsByTypeSchema,
  bookedHours: z.number().nonnegative(),
  categoryCounts: categoryCountSchema,
});

export const dayBreakdownPerCompetenceSchema = z.object({
  competence: medicalCompetenceSchema,
  anyTimeOfDayCategoryCounts: categoryCountSchema,
  anyTimeOfDayBookedHours: z.number().nonnegative(),
  day: partOfDayCapacitySchema.nullable(),
  evening: partOfDayCapacitySchema.nullable(),
  night: partOfDayCapacitySchema.nullable(),
});

export type DailyOverviewData = z.infer<typeof dailyOverviewDataSchema>;
export type DaySummaryData = z.infer<typeof daySummaryDataSchema>;
export type DayBreakdownData = z.infer<typeof dayBreakdownPerCompetenceSchema>;
export type PartOfDayCapacity = z.infer<typeof partOfDayCapacitySchema>;
export type ShiftsByType = z.infer<typeof shiftsByTypeSchema>;

const capacityKeys = {
  all: ["capacity"] as const,
  dailyOverview: (from: string, to: string) =>
    [...capacityKeys.all, "daily-overview", from, to] as const,
  daySummary: (date: string) =>
    [...capacityKeys.all, "day-summary", date] as const,
  dayBreakdown: (date: string) =>
    [...capacityKeys.all, "day-breakdown", date] as const,
};

const fetchDailyOverviews = async (from: string, to: string) => {
  const queryString = generateQueryString({ from, to });
  return await logisticsApi
    .get(`/capacity/daily-overview${queryString}`)
    .json(z.array(dailyOverviewDataSchema));
};

const fetchDaySummary = async (date: string) => {
  const queryString = generateQueryString({ date });
  return await logisticsApi
    .get(`/capacity/day-summary${queryString}`)
    .json(daySummaryDataSchema);
};

const fetchDayBreakdown = async (date: string) => {
  const queryString = generateQueryString({ date });
  return await logisticsApi
    .get(`/capacity/day-breakdown${queryString}`)
    .json(z.array(dayBreakdownPerCompetenceSchema));
};

export const useCapacityDailyOverviews = ({
  from,
  to,
}: {
  from: Date;
  to: Date;
}) => {
  const formattedFrom = calendarDateSchema.encode(from);
  const formattedTo = calendarDateSchema.encode(to);
  return useQuery({
    queryKey: capacityKeys.dailyOverview(formattedFrom, formattedTo),
    queryFn: () => fetchDailyOverviews(formattedFrom, formattedTo),
  });
};

export const useCapacityDaySummary = (date: Date) => {
  const formattedDate = calendarDateSchema.encode(date);
  return useQuery({
    queryKey: capacityKeys.daySummary(formattedDate),
    queryFn: () => fetchDaySummary(formattedDate),
  });
};

export const useCapacityDayBreakdown = (date: Date) => {
  const formattedDate = calendarDateSchema.encode(date);
  return useQuery({
    queryKey: capacityKeys.dayBreakdown(formattedDate),
    queryFn: () => fetchDayBreakdown(formattedDate),
  });
};
